import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import { getComentariosPorSiniestro, crearComentario } from '../../services/art/comentarioSiniestroService';
import { actualizarSiniestro, eliminarSiniestro, getSiniestroPorId } from '../../services/art/siniestroService';
import { getEtiquetasActivas, crearOActualizarEtiqueta } from '../../services/art/etiquetaService';
import { getLesiones } from '../../services/art/lesionService';
import '../../style/components/ModalHistorialEstados.css';
import '../../style/components/SiniestroStyles.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { coincideBusqueda } from '../../utils/textoComparable';

const ModalHistorialSiniestro = ({ isOpen, onClose, siniestroId, numeroSiniestro, siniestroData, onComentarioAdded, onSiniestroActualizado, onSiniestroEliminado }) => {
    const [comentarios, setComentarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [mostrarFormCambio, setMostrarFormCambio] = useState(false);
    const [editandoSiniestro, setEditandoSiniestro] = useState(false); // Modo edición inline
    const [confirmandoEliminar, setConfirmandoEliminar] = useState(false); // Confirmación inline

    // Estado para lesiones
    const [lesiones, setLesiones] = useState([]);

    // Estado local para los datos del siniestro mostrados (se actualiza cuando se edita)
    const [datosSiniestroActual, setDatosSiniestroActual] = useState(siniestroData);

    // Formulario de edición
    const [formEditar, setFormEditar] = useState({
        nroSiniestro: '',
        nroExpedienteSRT: '',
        idsLesiones: [],
        tipoContingencia: 'AT',
        comentario: ''
    });


    const obtenerIdsLesionesDesdeSiniestro = (s) => {
        if (Array.isArray(s?.idsLesiones) && s.idsLesiones.length > 0) {
            return s.idsLesiones.map(Number);
        }
        const idLegacy = s?.idLesion ?? s?.lesion?.idLesion ?? s?.lesion?.id;
        return idLegacy != null ? [Number(idLegacy)] : [];
    };

    // Formulario de comentario
    const [nuevaEtiqueta, setNuevaEtiqueta] = useState(null);
    const [etiquetasDisponibles, setEtiquetasDisponibles] = useState([]);
    const [etiquetaInputValue, setEtiquetaInputValue] = useState('');
    const [nuevoComentario, setNuevoComentario] = useState('');
    const [error, setError] = useState(null);
    const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
    const [colorNuevaEtiquetaCreable, setColorNuevaEtiquetaCreable] = useState('#808080');

    const tiposContingencia = [
        { value: 'AT', label: 'Accidente de Trabajo' },
        { value: 'EP', label: 'Enfermedad Profesional' },
        { value: 'EPNL', label: 'Enfermedad Profesional No Listada' }
    ];

    useEffect(() => {
        if (isOpen && siniestroId) {
            cargarHistorial();
            cargarEtiquetas();
            cargarListaLesiones();
            setDatosSiniestroActual(siniestroData);
            if (siniestroData) {
                setFormEditar({
                    nroSiniestro: siniestroData.numeroSiniestro || siniestroData.nroSiniestro || '',
                    nroExpedienteSRT: siniestroData.nroExpedienteSRT || '',
                    idsLesiones: obtenerIdsLesionesDesdeSiniestro(siniestroData),
                    tipoContingencia: siniestroData.tipoContingencia || 'AT',
                    comentario: siniestroData.comentario || ''
                });
            }
            setNuevaEtiqueta(null);
            setEtiquetaInputValue('');
            setNuevoComentario('');
        }

    }, [isOpen, siniestroId]);

    const cargarEtiquetas = async () => {
        try {
            const data = await getEtiquetasActivas();
            setEtiquetasDisponibles(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar etiquetas:', error);
            setEtiquetasDisponibles([]);
        }
    };

    const cargarListaLesiones = async () => {
        try {
            const data = await getLesiones();
            setLesiones(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar lesiones:', error);
            setLesiones([]);
        }
    };

    const cargarHistorial = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getComentariosPorSiniestro(siniestroId);
            setComentarios(data || []);
        } catch (error) {
            console.error('Error al cargar historial del siniestro:', error);
            setError('No se pudo cargar el historial del siniestro.');
            setComentarios([]);
        } finally {
            setLoading(false);
        }
    };

    // Crear nueva etiqueta dinámicamente
    const handleCrearNuevaEtiqueta = async () => {
        if (!etiquetaInputValue.trim()) {
            toast.error('El nombre de la etiqueta no puede estar vacío');
            return;
        }

        setSubmitting(true);
        try {
            const nuevaEtiquetaPayload = {
                nombre: etiquetaInputValue.trim(),
                color: colorNuevaEtiquetaCreable,
                activa: true
            };

            const etiquetaCreada = await crearOActualizarEtiqueta(nuevaEtiquetaPayload);

            // Actualizar lista de etiquetas disponibles
            setEtiquetasDisponibles([...etiquetasDisponibles, etiquetaCreada]);
            setNuevaEtiqueta(etiquetaCreada);
            setEtiquetaInputValue(etiquetaCreada.nombre);
            setMostrarSugerencias(false);

            toast.success(`Etiqueta "${etiquetaInputValue}" creada correctamente`);
        } catch (error) {
            console.error('Error al crear etiqueta:', error);
            toast.error('Error al crear la etiqueta: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };


    const handleEditarSiniestro = async () => {
        if (!formEditar.nroSiniestro.trim()) {
            toast.error('El número de siniestro es obligatorio');
            return;
        }

        setSubmitting(true);
        try {
            const idsLesionesPayload = Array.isArray(formEditar.idsLesiones)
                ? formEditar.idsLesiones.map(Number).filter((n) => !Number.isNaN(n))
                : [];

            const payload = {
                numeroSiniestro: formEditar.nroSiniestro.trim(),
                nroExpedienteSRT: formEditar.nroExpedienteSRT.trim() || null,
                idsLesiones: idsLesionesPayload,
                tipoContingencia: formEditar.tipoContingencia,
                comentario: formEditar.comentario.trim()
            };

            // Guardar en backend
            await actualizarSiniestro(siniestroId, payload);
            toast.success('Siniestro actualizado correctamente');

            // Re-fetch del siniestro completo para tener los datos canónicos.
            let siniestroFresco;
            try {
                siniestroFresco = await getSiniestroPorId(siniestroId);
            } catch (e) {
                console.warn('No se pudo re-fetchar el siniestro tras editar; uso merge local.', e);
                siniestroFresco = null;
            }


            const idsLesionesCanon = Array.isArray(siniestroFresco?.idsLesiones)
                ? siniestroFresco.idsLesiones.map(Number)
                : idsLesionesPayload;
            const nombresLesionesCanon = Array.isArray(siniestroFresco?.nombresLesiones) && siniestroFresco.nombresLesiones.length > 0
                ? siniestroFresco.nombresLesiones
                : idsLesionesCanon
                    .map((id) => lesiones.find((l) => Number(l.idLesion ?? l.id) === id))
                    .filter(Boolean)
                    .map((l) => l.descripcion || l.nombre)
                    .filter(Boolean);

            const datosActualizados = {
                ...datosSiniestroActual,
                ...(siniestroFresco || {}),
                numeroSiniestro: siniestroFresco?.nroSiniestro || formEditar.nroSiniestro.trim(),
                nroSiniestro: siniestroFresco?.nroSiniestro || formEditar.nroSiniestro.trim(),
                nroExpedienteSRT: siniestroFresco?.nroExpedienteSRT ?? (formEditar.nroExpedienteSRT.trim() || null),
                idsLesiones: idsLesionesCanon,
                nombresLesiones: nombresLesionesCanon,
                idLesion: idsLesionesCanon[0] ?? null,
                nombreLesion: nombresLesionesCanon[0] ?? null,
                lesion: nombresLesionesCanon[0]
                    ? { idLesion: idsLesionesCanon[0], nombre: nombresLesionesCanon[0], descripcion: nombresLesionesCanon[0] }
                    : null,
                lesionDescripcion: nombresLesionesCanon[0] ?? null,
                tipoContingencia: siniestroFresco?.tipoContingencia || formEditar.tipoContingencia,
                comentario: siniestroFresco?.comentario ?? formEditar.comentario.trim()
            };

            flushSync(() => {
                setEditandoSiniestro(false);
                setDatosSiniestroActual({ ...datosActualizados });
                setFormEditar({
                    nroSiniestro: datosActualizados.nroSiniestro || '',
                    nroExpedienteSRT: datosActualizados.nroExpedienteSRT || '',
                    idsLesiones: idsLesionesCanon,
                    tipoContingencia: datosActualizados.tipoContingencia || 'AT',
                    comentario: datosActualizados.comentario || ''
                });
            });

            if (onSiniestroActualizado) {
                onSiniestroActualizado();
            }
        } catch (error) {
            console.error('Error al actualizar siniestro:', error);
            toast.error('Error al actualizar siniestro: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEliminarSiniestro = async () => {
        setSubmitting(true);
        try {
            await eliminarSiniestro(siniestroId);
            toast.success('Siniestro eliminado correctamente');
            setConfirmandoEliminar(false);
            onClose();
            if (onSiniestroEliminado) {
                onSiniestroEliminado();
            }
        } catch (error) {
            console.error('Error al eliminar siniestro:', error);
            toast.error('Error al eliminar siniestro: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar que haya comentario y etiqueta
        if (!nuevoComentario.trim()) {
            toast.error('El comentario es obligatorio');
            return;
        }

        if (!etiquetaInputValue.trim()) {
            toast.error('Debe seleccionar una etiqueta');
            return;
        }

        setSubmitting(true);
        try {
            // Find matched tag if any
            const matchedTag = nuevaEtiqueta || etiquetasDisponibles.find(et => et.nombre.toLowerCase() === etiquetaInputValue.trim().toLowerCase());

            // Construir payload simple para el backend actual
            const payload = {
                idSiniestro: siniestroId,
                idEtiqueta: matchedTag?.idEtiqueta ?? matchedTag?.id ?? null,
                etiqueta: etiquetaInputValue.trim(), // fallback compatibilidad
                comentario: nuevoComentario.trim()
            };

            await crearComentario(payload);
            toast.success('Comentario agregado correctamente');

            // Limpiar formulario
            setNuevoComentario('');
            setEtiquetaInputValue('');
            setNuevaEtiqueta(null);
            setMostrarFormCambio(false);

            // Recargar datos
            await cargarHistorial();

            if (onComentarioAdded) {
                onComentarioAdded();
            }
        } catch (error) {
            console.error('Error al crear comentario:', error);
            toast.error('Error al guardar el comentario: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    // Formatear fecha similar a HistorialTramite
    const formatFecha = (fechaString) => {
        if (!fechaString) return '-';
        try {
            const date = new Date(fechaString);
            return date.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return fechaString;
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target.classList.contains('modal-historial-backdrop')) {
            onClose();
        }
    };

    if (!isOpen) return null;

    // Formatear etiqueta como badge
    const renderEtiqueta = (comentario) => {
        // El DTO devuelve 'etiqueta' como string, no como objeto
        const nombre = comentario.etiquetaNombre || comentario.etiqueta || '-';
        let color = comentario.etiquetaColor || '#808080';

        if (nombre === 'APROBADO') color = '#22c55e';    // verde
        if (nombre === 'RECHAZADO') color = '#ef4444';   // rojo
        if (nombre === 'FAVORABLE') color = '#16a34a';   // verde oscuro
        if (nombre === 'DESFAVORABLE') color = '#ea580c'; // naranja
        if (nombre === 'GENERAL') color = '#6366f1';     // índigo

        return (
            <span
                style={{
                    backgroundColor: color,
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    display: 'inline-block'
                }}
            >
                {nombre}
            </span>
        );
    };

    return (
        <div className="modal-historial-backdrop" onClick={handleBackdropClick}>
            <div className="modal-historial-content" style={{ maxWidth: '1180px', width: '95vw', zIndex: 1050 }}>
                {/* Header */}
                <div className="modal-historial-header">
                    <div className="modal-title-section">
                        <h2>Historial del Siniestro</h2>
                        <p className="modal-subtitle">Siniestro #{numeroSiniestro || siniestroId}</p>
                    </div>
                    <button
                        className="modal-close-button"
                        onClick={onClose}
                        aria-label="Cerrar modal"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Body */}
                <div className="modal-historial-body" style={{ width: '100%' }}>
                    {/* === PANEL DE INFORMACIÓN TÉCNICA DEL SINIESTRO === */}
                    {datosSiniestroActual && (
                        <div className="siniestro-info-panel">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h5 className="panel-title" style={{ margin: 0 }}>Información General del Siniestro</h5>
                                {!editandoSiniestro && !confirmandoEliminar && (
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setEditandoSiniestro(true)}
                                            title="Editar información del siniestro"
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: '#3d5a80',
                                                border: '1px solid #c8d6ea',
                                                fontSize: '0.85rem',
                                                padding: '0.35rem 0.7rem',
                                                borderRadius: '0.35rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                fontWeight: '500'
                                            }}
                                            disabled={submitting}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#e8eef7';
                                                e.target.style.borderColor = '#a8c1e8';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'transparent';
                                                e.target.style.borderColor = '#c8d6ea';
                                            }}
                                        >
                                            <EditIcon fontSize="small" style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
                                            Editar
                                        </button>
                                        <button
                                            onClick={() => setConfirmandoEliminar(true)}
                                            title="Eliminar este siniestro"
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: '#9b4d4d',
                                                border: '1px solid #e8c9c9',
                                                fontSize: '0.85rem',
                                                padding: '0.35rem 0.7rem',
                                                borderRadius: '0.35rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                fontWeight: '500'
                                            }}
                                            disabled={submitting}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#f7e9e9';
                                                e.target.style.borderColor = '#daa5a5';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'transparent';
                                                e.target.style.borderColor = '#e8c9c9';
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
                                            Eliminar
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!editandoSiniestro && !confirmandoEliminar ? (
                                // MODO VISUALIZACIÓN
                                <div className="info-grid" style={{ opacity: 1, transition: 'opacity 0.3s ease' }}>
                                    <div className="info-item">
                                        <span className="label">Nro. Siniestro:</span>
                                        <span className="value">{datosSiniestroActual.numeroSiniestro || datosSiniestroActual.nroSiniestro || '-'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">Nro. Expediente SRT:</span>
                                        <span className="value">{datosSiniestroActual.nroExpedienteSRT || '-'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">Lesiones:</span>
                                        <span className="value">{(() => {
                                            const lista = Array.isArray(datosSiniestroActual.nombresLesiones) && datosSiniestroActual.nombresLesiones.length > 0
                                                ? datosSiniestroActual.nombresLesiones
                                                : (() => {
                                                    const ids = obtenerIdsLesionesDesdeSiniestro(datosSiniestroActual);
                                                    if (ids.length === 0) {
                                                        const legacy = datosSiniestroActual.lesion?.descripcion || datosSiniestroActual.lesionDescripcion;
                                                        return legacy ? [legacy] : [];
                                                    }
                                                    return ids
                                                        .map((id) => lesiones.find((l) => Number(l.idLesion ?? l.id) === id))
                                                        .filter(Boolean)
                                                        .map((l) => l.descripcion || l.nombre)
                                                        .filter(Boolean);
                                                })();
                                            return lista.length > 0 ? lista.join(', ') : '-';
                                        })()}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">Tipo de Contingencia:</span>
                                        <span className="value">
                                            {datosSiniestroActual.tipoContingencia === 'AT' && 'Accidente de Trabajo'}
                                            {datosSiniestroActual.tipoContingencia === 'EP' && 'Enfermedad Profesional'}
                                            {datosSiniestroActual.tipoContingencia === 'EPNL' && 'Enfermedad Profesional No Listada'}
                                            {!datosSiniestroActual.tipoContingencia && '-'}
                                        </span>
                                    </div>
                                    <div className="info-item full-width">
                                        <span className="label">Comentario Principal:</span>
                                        <span className="value">{datosSiniestroActual.comentario || '(Sin comentarios)'}</span>
                                    </div>
                                </div>
                            ) : editandoSiniestro ? (
                                // MODO EDICIÓN IN-PLACE (Réplica editable)
                                <div style={{ opacity: 1, transition: 'opacity 0.3s ease' }}>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <span className="label">Nro. Siniestro:</span>
                                            <input
                                                type="text"
                                                value={formEditar.nroSiniestro}
                                                onChange={(e) => setFormEditar({ ...formEditar, nroSiniestro: e.target.value })}
                                                placeholder="Número de siniestro"
                                                disabled={submitting}
                                                style={{ width: '100%',
                                                    padding: '0.35rem 0.5rem',
                                                    border: '1px solid #d0d5e8',
                                                    borderRadius: '0.3rem', 
                                                    fontFamily: 'inherit',
                                                    transition: 'border-color 0.2s ease',
                                                    backgroundColor: '#fafbfc' }}
                                                onFocus={(e) => e.target.style.borderColor = '#7a8bc7'}
                                                onBlur={(e) => e.target.style.borderColor = '#d0d5e8'}
                                            />
                                        </div>
                                        <div className="info-item">
                                            <span className="label">Nro. Expediente SRT:</span>
                                            <input
                                                type="text"
                                                value={formEditar.nroExpedienteSRT}
                                                onChange={(e) => setFormEditar({ ...formEditar, nroExpedienteSRT: e.target.value })}
                                                placeholder="Número de expediente"
                                                disabled={submitting}
                                                style={{ width: '100%',
                                                    padding: '0.35rem 0.5rem',
                                                    border: '1px solid #d0d5e8',
                                                    borderRadius: '0.3rem', 
                                                    fontFamily: 'inherit',
                                                    transition: 'border-color 0.2s ease',
                                                    backgroundColor: '#fafbfc' }}
                                                onFocus={(e) => e.target.style.borderColor = '#7a8bc7'}
                                                onBlur={(e) => e.target.style.borderColor = '#d0d5e8'}
                                            />
                                        </div>
                                        <div className="info-item">
                                            <span className="label">Lesiones:</span>
                                            <select
                                                multiple
                                                value={formEditar.idsLesiones.map(String)}
                                                onChange={(e) => {
                                                    const seleccion = Array.from(e.target.selectedOptions)
                                                        .map((opt) => Number(opt.value))
                                                        .filter((n) => !Number.isNaN(n));
                                                    setFormEditar({ ...formEditar, idsLesiones: seleccion });
                                                }}
                                                disabled={submitting}
                                                size={Math.min(6, Math.max(3, lesiones.length))}
                                                style={{ width: '100%',
                                                    padding: '0.35rem 0.5rem',
                                                    border: '1px solid #d0d5e8',
                                                    borderRadius: '0.3rem', 
                                                    fontFamily: 'inherit',
                                                    transition: 'border-color 0.2s ease',
                                                    backgroundColor: '#fafbfc',
                                                    cursor: 'pointer' }}
                                                onFocus={(e) => e.target.style.borderColor = '#7a8bc7'}
                                                onBlur={(e) => e.target.style.borderColor = '#d0d5e8'}
                                            >
                                                {lesiones.map((lesion) => (
                                                    <option key={lesion.idLesion || lesion.id} value={lesion.idLesion || lesion.id}>
                                                        {lesion.descripcion || lesion.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                            <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                                Ctrl + click para seleccionar varias
                                            </small>
                                        </div>
                                        <div className="info-item">
                                            <span className="label">Tipo de Contingencia:</span>
                                            <select
                                                value={formEditar.tipoContingencia}
                                                onChange={(e) => setFormEditar({ ...formEditar, tipoContingencia: e.target.value })}
                                                disabled={submitting}
                                                style={{ width: '100%',
                                                    padding: '0.35rem 0.5rem',
                                                    border: '1px solid #d0d5e8',
                                                    borderRadius: '0.3rem', 
                                                    fontFamily: 'inherit',
                                                    transition: 'border-color 0.2s ease',
                                                    backgroundColor: '#fafbfc',
                                                    cursor: 'pointer' }}
                                                onFocus={(e) => e.target.style.borderColor = '#7a8bc7'}
                                                onBlur={(e) => e.target.style.borderColor = '#d0d5e8'}
                                            >
                                                <option value="AT">Accidente de Trabajo</option>
                                                <option value="EP">Enfermedad Profesional</option>
                                                <option value="EPNL">Enfermedad Profesional No Listada</option>
                                            </select>
                                        </div>
                                        <div className="info-item full-width">
                                            <span className="label">Comentario Principal:</span>
                                            <textarea
                                                value={formEditar.comentario}
                                                onChange={(e) => setFormEditar({ ...formEditar, comentario: e.target.value })}
                                                placeholder="Comentario sobre el siniestro"
                                                rows="3"
                                                maxLength="500"
                                                disabled={submitting}
                                                style={{ width: '100%',
                                                    padding: '0.35rem 0.5rem',
                                                    border: '1px solid #d0d5e8',
                                                    borderRadius: '0.3rem', 
                                                    fontFamily: 'inherit',
                                                    transition: 'border-color 0.2s ease',
                                                    backgroundColor: '#fafbfc',
                                                    resize: 'vertical' }}
                                                onFocus={(e) => e.target.style.borderColor = '#7a8bc7'}
                                                onBlur={(e) => e.target.style.borderColor = '#d0d5e8'}
                                            />
                                            <small style={{ display: 'block', marginTop: '0.25rem', color: '#999' }}>
                                                {formEditar.comentario.length}/500 caracteres
                                            </small>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setEditandoSiniestro(false)}
                                            disabled={submitting}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                border: '1px solid #d0d5e8',
                                                borderRadius: '0.35rem',
                                                backgroundColor: '#f5f6f8',
                                                color: '#555',
                                                cursor: submitting ? 'not-allowed' : 'pointer',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease',
                                                opacity: submitting ? 0.6 : 1
                                            }}
                                            onMouseEnter={(e) => !submitting && (e.target.style.backgroundColor = '#e8eaef')}
                                            onMouseLeave={(e) => !submitting && (e.target.style.backgroundColor = '#f5f6f8')}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleEditarSiniestro}
                                            disabled={submitting}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                border: 'none',
                                                borderRadius: '0.35rem',
                                                backgroundColor: '#3d5a80',
                                                color: '#fff',
                                                cursor: submitting ? 'not-allowed' : 'pointer',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease',
                                                opacity: submitting ? 0.7 : 1
                                            }}
                                            onMouseEnter={(e) => !submitting && (e.target.style.backgroundColor = '#2c4563')}
                                            onMouseLeave={(e) => !submitting && (e.target.style.backgroundColor = '#3d5a80')}
                                        >
                                            {submitting ? (
                                                <>
                                                    <i className="bi bi-hourglass-split me-2"></i>
                                                    Guardando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check-lg me-2"></i>
                                                    Guardar Cambios
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : confirmandoEliminar ? (
                                // CONFIRMACIÓN DE ELIMINACIÓN INTEGRADA
                                <div style={{
                                    backgroundColor: '#fef8f5',
                                    border: '1px solid #e8b5b5',
                                    borderRadius: '0.4rem',
                                    padding: '1.25rem',
                                    marginBottom: '1rem'
                                }}>
                                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                        <div style={{ color: '#d9534f', fontSize: '1.4rem', marginTop: '0.2rem' }}>
                                            <i className="bi bi-exclamation-circle"></i>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h6 style={{ margin: '0 0 0.5rem 0', color: '#8b4545', fontWeight: '600' }}>
                                                ¿Desea eliminar este siniestro?
                                            </h6>
                                            <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem', lineHeight: '1.4' }}>
                                                Esta acción no se puede deshacer. Se eliminarán todos los comentarios y registros asociados al siniestro.
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setConfirmandoEliminar(false)}
                                            disabled={submitting}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                border: '1px solid #d0d5e8',
                                                borderRadius: '0.35rem',
                                                backgroundColor: '#f5f6f8',
                                                color: '#555',
                                                cursor: submitting ? 'not-allowed' : 'pointer',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease',
                                                opacity: submitting ? 0.6 : 1
                                            }}
                                            onMouseEnter={(e) => !submitting && (e.target.style.backgroundColor = '#e8eaef')}
                                            onMouseLeave={(e) => !submitting && (e.target.style.backgroundColor = '#f5f6f8')}
                                        >
                                            No, Cancelar
                                        </button>
                                        <button
                                            onClick={handleEliminarSiniestro}
                                            disabled={submitting}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                border: 'none',
                                                borderRadius: '0.35rem',
                                                backgroundColor: '#d9534f',
                                                color: '#fff',
                                                cursor: submitting ? 'not-allowed' : 'pointer',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease',
                                                opacity: submitting ? 0.7 : 1
                                            }}
                                            onMouseEnter={(e) => !submitting && (e.target.style.backgroundColor = '#b84543')}
                                            onMouseLeave={(e) => !submitting && (e.target.style.backgroundColor = '#d9534f')}
                                        >
                                            {submitting ? (
                                                <>
                                                    <i className="bi bi-hourglass-split me-2"></i>
                                                    Eliminando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-trash me-2"></i>
                                                    Sí, Eliminar Siniestro
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* === ACTION BAR === */}
                    <div className="historial-action-bar">
                        <button
                            className="btn-cambiar-estado"
                            onClick={() => setMostrarFormCambio(!mostrarFormCambio)}
                        >
                            <i className={`bi ${mostrarFormCambio ? 'bi-x-circle' : 'bi-plus-circle'} me-2`}></i>
                            {mostrarFormCambio ? 'Cancelar' : 'Agregar Comentario'}
                        </button>
                    </div>

                    {/* Formulario de Alta */}
                    {mostrarFormCambio && (
                        <div className="cambio-estado-form-container">
                            <h4>Nuevo Registro de Bitácora</h4>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label htmlFor="etiqueta">Etiqueta *</label>
                                    <div id="etiqueta-input-container" style={{ position: 'relative' }}>
                                        <input
                                            id="etiqueta"
                                            type="text"
                                            value={etiquetaInputValue}
                                            onChange={(e) => {
                                                setEtiquetaInputValue(e.target.value);
                                                setMostrarSugerencias(true);
                                                const encontrada = etiquetasDisponibles.find(
                                                    et => et.nombre.toLowerCase() === e.target.value.toLowerCase()
                                                );
                                                setNuevaEtiqueta(encontrada || null);
                                            }}
                                            onFocus={() => setMostrarSugerencias(true)}
                                            placeholder="Buscar o crear etiqueta..."
                                            disabled={submitting}
                                            autoComplete="off"
                                            style={{ width: '100%',
                                                padding: '0.5rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '0.3rem' }}
                                        />

                                        {/* Dropdown de sugerencias */}
                                        {mostrarSugerencias && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                backgroundColor: '#fff',
                                                border: '1px solid #ddd',
                                                borderTop: 'none',
                                                borderRadius: '0 0 0.3rem 0.3rem',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                zIndex: 10,
                                                boxShadow: 'var(--shadow-sm)'
                                            }}>
                                                {/* Etiquetas coincidentes */}
                                                {etiquetasDisponibles
                                                    .filter(et => coincideBusqueda(et.nombre, etiquetaInputValue))
                                                    .map(etiqueta => (
                                                        <div
                                                            key={etiqueta.idEtiqueta || etiqueta.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setNuevaEtiqueta(etiqueta);
                                                                setEtiquetaInputValue(etiqueta.nombre);
                                                                setMostrarSugerencias(false);
                                                            }}
                                                            style={{
                                                                padding: '0.5rem',
                                                                cursor: 'pointer',
                                                                borderBottom: '1px solid #f0f0f0',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.5rem'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                                        >
                                                            <span
                                                                style={{
                                                                    display: 'inline-block',
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: etiqueta.color || '#808080'
                                                                }}
                                                            />
                                                            {etiqueta.nombre}
                                                        </div>
                                                    ))}

                                                {/* Opción crear nueva */}
                                                {etiquetaInputValue && !etiquetasDisponibles.some(
                                                    et => et.nombre.toLowerCase() === etiquetaInputValue.toLowerCase()
                                                ) && (
                                                    <>
                                                        <div style={{
                                                            padding: '0.75rem 0.5rem',
                                                            cursor: 'pointer',
                                                            backgroundColor: '#f0f7ff',
                                                            borderTop: '1px solid #ddd',
                                                            fontWeight: '500',
                                                            color: '#3d5a80',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '1rem',
                                                            justifyContent: 'flex-start'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8eef7'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <i className="bi bi-plus-circle" style={{ fontSize: '1rem' }}></i>
                                                                <span>Crear "{etiquetaInputValue}"</span>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                                                                <label style={{ fontSize: '0.8rem', marginRight: '0.3rem', color: '#666', fontWeight: 'normal' }}>
                                                                    Color:
                                                                </label>
                                                                <input
                                                                    type="color"
                                                                    value={colorNuevaEtiquetaCreable}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        setColorNuevaEtiquetaCreable(e.target.value);
                                                                    }}
                                                                    disabled={submitting}
                                                                    style={{
                                                                        width: '30px',
                                                                        height: '24px',
                                                                        border: '1px solid #ccc',
                                                                        borderRadius: '0.2rem',
                                                                        cursor: submitting ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div
                                                            onClick={handleCrearNuevaEtiqueta}
                                                            style={{
                                                                padding: '0.5rem',
                                                                cursor: submitting ? 'not-allowed' : 'pointer',
                                                                backgroundColor: '#e8eef7',
                                                                textAlign: 'center',
                                                                fontSize: '0.9rem',
                                                                fontWeight: '600',
                                                                color: '#3d5a80',
                                                                borderTop: '1px solid #ddd',
                                                                opacity: submitting ? 0.6 : 1,
                                                                transition: 'background-color 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#dae1f0')}
                                                            onMouseLeave={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#e8eef7')}
                                                        >
                                                            Crear Nueva Etiqueta
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                        Escriba para buscar o crear una nueva etiqueta
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="comentario">Comentario/Detalle *</label>
                                    <textarea
                                        id="comentario"
                                        value={nuevoComentario}
                                        onChange={(e) => setNuevoComentario(e.target.value)}
                                        rows="4"
                                        placeholder="Escriba el detalle del comentario..."
                                        disabled={submitting}
                                        required
                                        maxLength="1000"
                                    />
                                    <small className="char-count">
                                        {nuevoComentario.length}/1000 caracteres
                                    </small>
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMostrarFormCambio(false);
                                            setNuevoComentario('');
                                            setEtiquetaInputValue('');
                                            setNuevaEtiqueta(null);
                                        }}
                                        className="btn-cancelar"
                                        disabled={submitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting || !nuevoComentario.trim() || !etiquetaInputValue}
                                        className="btn-confirmar"
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="bi bi-hourglass-split me-2"></i>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg me-2"></i>
                                                Guardar Registro
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Estados de carga y error */}
                    {loading && (
                        <div className="historial-loading">
                            <div className="loading-spinner"></div>
                            <p>Cargando historial del siniestro...</p>
                        </div>
                    )}

                    {error && (
                        <div className="historial-error">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            <div>
                                <strong>Error al cargar el historial</strong>
                                <p>{error}</p>
                            </div>
                            <button
                                onClick={cargarHistorial}
                                className="btn-reintentar"
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {/* Tabla de historial */}
                    {!loading && !error && comentarios.length === 0 && (
                        <div className="historial-empty">
                            <i className="bi bi-inbox"></i>
                            <h4>No hay registros en la bitácora</h4>
                            <p>Aún no se han agregado comentarios a este siniestro.</p>
                        </div>
                    )}

                    {!loading && !error && comentarios.length > 0 && (
                        <div className="historial-table-container">
                            <div className="table-responsive">
                                <table className="historial-table">
                                    <thead>
                                        <tr>
                                            <th style={{width: '20%'}}>Fecha</th>
                                            <th style={{width: '20%'}}>Etiqueta</th>
                                            <th style={{width: '60%'}}>Detalle</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {comentarios.map((c, index) => (
                                            <tr key={c.idComentarioSiniestro || index}>
                                                <td>
                                                    <div className="fecha-hora">
                                                        {formatFecha(c.fecha)}
                                                    </div>
                                                </td>
                                                <td>
                                                    {renderEtiqueta(c)}
                                                </td>
                                                <td className="comentario-cell">
                                                    <div className="comentario-text" title={c.comentario}>
                                                        {c.comentario}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="historial-summary">
                                <div className="summary-item">
                                    <span className="summary-label">Total de registros:</span>
                                    <span className="summary-value">{comentarios.length}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-historial-footer">
                    <button
                        onClick={onClose}
                        className="btn-cerrar-modal"
                        disabled={submitting}
                    >
                        <i className="bi bi-x-lg me-2"></i>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModalHistorialSiniestro;
